/**
 * Fixtures do **Resumo Diário** — modelo v1: turno × grupo × valor.
 *
 * Alinhado a WAYBEE.031 / SOCORRENCIAALUNO:
 * - momento = CODPERIODO (MANHA | ALMOCO | MEIO_TARDE)
 * - eixo = grupo de ocorrência (Alimentação, Higiene, Sono, Conteúdo)
 * - conceito = tipo (Ruim | Regular | Bom)
 */

import moment from "moment";
import { buildDemoStudentsForClass } from "./buildDemoStudents";
import { DEMO_SCENARIO, DEMO_SCENARIO_CLASS_ID } from "./demoScenario";

export const DEMO_DAILY_SUMMARY_REF_ID = "DEMO-T1::DAILY_SUMMARY";

const MOMENTS = [
  {
    id: "MANHA",
    name: "Manhã",
    startMinutes: 8 * 60,
    endMinutes: 12 * 60
  },
  {
    id: "ALMOCO",
    name: "Almoço",
    startMinutes: 12 * 60,
    endMinutes: 14 * 60
  },
  {
    id: "MEIO_TARDE",
    name: "Meio da tarde",
    startMinutes: 14 * 60,
    endMinutes: 18 * 60
  }
];

/** Grupos por turno (espelha vínculos + ICON/COR da WAYBEE.031). */
const GROUPS_BY_MOMENT = {
  MANHA: [
    { code: "9", name: "Alimentação", icon: "restaurant", color: "#FB8C00" },
    { code: "10", name: "Higiene", icon: "spa", color: "#26A69A" },
    { code: "11", name: "Sono", icon: "hotel", color: "#7E57C2" },
    { code: "12", name: "Conteúdo", icon: "menu_book", color: "#42A5F5" }
  ],
  ALMOCO: [
    { code: "9", name: "Alimentação", icon: "restaurant", color: "#FB8C00" }
  ],
  MEIO_TARDE: [
    { code: "9", name: "Alimentação", icon: "restaurant", color: "#FB8C00" },
    { code: "10", name: "Higiene", icon: "spa", color: "#26A69A" },
    { code: "11", name: "Sono", icon: "hotel", color: "#7E57C2" },
    { code: "12", name: "Conteúdo", icon: "menu_book", color: "#42A5F5" }
  ]
};

const CONCEPTS = [
  {
    id: "ds_ruim",
    label: "Ruim",
    icon: "sentiment_very_dissatisfied",
    color: "#ffccbc",
    tipoLabel: "Ruim"
  },
  {
    id: "ds_regular",
    label: "Regular",
    icon: "sentiment_neutral",
    color: "#fff9c4",
    tipoLabel: "Regular"
  },
  {
    id: "ds_bom",
    label: "Bom",
    icon: "sentiment_very_satisfied",
    color: "#c8e6c9",
    tipoLabel: "Bom"
  }
];

const CONCEPT_ROTATION = ["ds_bom", "ds_bom", "ds_regular", "ds_bom", "ds_ruim"];

function makeIndex(periodId, group) {
  return {
    id: `${periodId}-${group.code}`,
    name: "Avaliação",
    TIPOETAPA: "N",
    CODETAPA: "DS",
    CODPROVA: group.code,
    codOcorrenciaGrupo: group.code
  };
}

function axisForMoment(momentId) {
  const groups = GROUPS_BY_MOMENT[momentId] || [];
  return groups.map(g => ({
    name: g.name,
    icon: g.icon,
    color: g.color,
    indexes: [makeIndex(momentId, g)]
  }));
}

function dateFromKey(key) {
  if (!key) return moment().startOf("day");
  const m = moment(key, "YYYY-MM-DD");
  return m.isValid() ? m.startOf("day") : moment().startOf("day");
}

function currentMomentId(now = moment()) {
  const minutes = now.hours() * 60 + now.minutes();
  const found = MOMENTS.find(
    m => minutes >= m.startMinutes && minutes <= m.endMinutes
  );
  return found ? found.id : null;
}

function hmFromMinutes(total) {
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export function buildDemoDSPeriods() {
  const currentId = currentMomentId();
  return MOMENTS.map(m => ({
    id: m.id,
    name: m.name,
    startTime: hmFromMinutes(m.startMinutes),
    endTime: hmFromMinutes(m.endMinutes),
    current: m.id === currentId
  }));
}

export function buildDemoDSConcepts() {
  return CONCEPTS;
}

function buildStudentsMatrix(axis, fillFactor = 1, offset = 0) {
  const students = buildDemoStudentsForClass(DEMO_SCENARIO_CLASS_ID);
  const allIndexes = axis.reduce((acc, ax) => acc.concat(ax.indexes), []);

  return students.map((s, i) => {
    const shouldFill = i / students.length < fillFactor;
    const concepts = allIndexes.map((ix, j) => {
      if (!shouldFill) return { id: ix.id, value: null };
      const value =
        CONCEPT_ROTATION[(i + j + offset) % CONCEPT_ROTATION.length];
      return { id: ix.id, value };
    });
    return {
      rollCallNum: i + 1,
      ra: s.ra,
      name: s.name,
      photo: s.photo,
      absense: 0,
      concepts
    };
  });
}

export function buildDemoDSReportData(dateKey, momentId) {
  const axis = axisForMoment(momentId);
  const offset = MOMENTS.findIndex(m => m.id === momentId);
  const baseDate = dateFromKey(dateKey);
  const todayKey = moment().format("YYYY-MM-DD");
  const isPast = baseDate.isBefore(moment().startOf("day"));
  const isToday = dateKey === todayKey;

  let fillFactor = 0;
  if (isPast) fillFactor = 1;
  else if (isToday) {
    const now = moment();
    const minutes = now.hours() * 60 + now.minutes();
    const momentDef = MOMENTS.find(m => m.id === momentId);
    if (!momentDef) fillFactor = 0;
    else if (minutes >= momentDef.endMinutes) fillFactor = 1;
    else if (minutes >= momentDef.startMinutes) {
      const elapsed = minutes - momentDef.startMinutes;
      const total = momentDef.endMinutes - momentDef.startMinutes;
      fillFactor = Math.max(0.3, Math.min(0.85, elapsed / total));
    } else {
      fillFactor = 0;
    }
  }

  return {
    axis: axis.map(ax => ({
      name: ax.name,
      icon: ax.icon,
      color: ax.color,
      indexes: ax.indexes
    })),
    students: buildStudentsMatrix(axis, fillFactor, offset === -1 ? 0 : offset)
  };
}

function computeFilledPercent(data) {
  const totalStudents = data.students.length;
  const totalIndexes = data.axis.reduce(
    (acc, ax) => acc + ax.indexes.length,
    0
  );
  if (totalStudents === 0 || totalIndexes === 0) return 0;
  const filled = data.students.reduce((acc, s) => {
    const n = (s.concepts || []).filter(c => c.value).length;
    return acc + n;
  }, 0);
  return Math.round((filled / (totalStudents * totalIndexes)) * 100) / 100;
}

function buildCard(classId, dateKey, dateLabel, momentDef, updatedAt) {
  const data = buildDemoDSReportData(dateKey, momentDef.id);
  const startTime =
    momentDef.startTime ||
    (momentDef.startMinutes != null
      ? hmFromMinutes(momentDef.startMinutes)
      : null);
  const endTime =
    momentDef.endTime ||
    (momentDef.endMinutes != null
      ? hmFromMinutes(momentDef.endMinutes)
      : null);
  return {
    summaryKey: `${dateKey}::${momentDef.id}`,
    classId,
    date: dateKey,
    dateLabel,
    moment: {
      id: momentDef.id,
      name: momentDef.name,
      startTime,
      endTime,
      current: !!momentDef.current
    },
    studentsCount: data.students.length,
    indexesCount: data.axis.reduce((acc, ax) => acc + ax.indexes.length, 0),
    filledPercent: computeFilledPercent(data),
    updatedAt
  };
}

function demoUpdatedHourForMoment(momentId) {
  if (momentId === "MANHA") return 11;
  if (momentId === "ALMOCO") return 13;
  return 17;
}

export function buildDemoDSList(classId) {
  if (classId !== DEMO_SCENARIO_CLASS_ID) return [];
  const scenario = DEMO_SCENARIO.dailySummary || {};
  if (!scenario.seedExistingSummaries) return [];

  const today = moment().startOf("day");
  const todayKey = today.format("YYYY-MM-DD");
  const yesterday = today.clone().subtract(1, "day");
  const yesterdayKey = yesterday.format("YYYY-MM-DD");
  const periods = buildDemoDSPeriods();

  const todayCards = periods.map(momentRow =>
    buildCard(
      classId,
      todayKey,
      "Hoje",
      momentRow,
      today
        .clone()
        .add(demoUpdatedHourForMoment(momentRow.id), "hours")
        .toISOString()
    )
  );

  const yesterdayCards = periods.map(momentRow =>
    buildCard(
      classId,
      yesterdayKey,
      "Ontem",
      { ...momentRow, current: false },
      yesterday
        .clone()
        .add(demoUpdatedHourForMoment(momentRow.id), "hours")
        .toISOString()
    )
  );

  return [...todayCards, ...yesterdayCards];
}

export function buildDemoDSDateOptions() {
  const base = moment().startOf("day");
  const options = [];
  for (let offset = -7; offset <= 0; offset += 1) {
    const d = base.clone().add(offset, "day");
    let name = d.format("DD/MM/YYYY");
    if (offset === 0) name = `Hoje (${d.format("DD/MM/YYYY")})`;
    else if (offset === -1) name = `Ontem (${d.format("DD/MM/YYYY")})`;
    options.push({
      id: d.format("YYYY-MM-DD"),
      name,
      current: offset === 0
    });
  }
  return options;
}
